<template>
    <!-- ITEMS -->
    <div class="items">
        <Input v-model="searchQuery" :disabled="!!error" class="query-input" placeholder="Filter results" />

        <Spin v-if="isLoading" fix />

        <div v-else-if="error" class="info-text">{{ error }}</div>

        <div v-else-if="documents.length === 0" class="info-text">
            {{ `No documents found ${searchQuery ? "for the query" : ""}.` }}
        </div>

        <Grid v-else :col="4" :border="false" :hover="false" padding="8px">
            <GridItem v-for="item in documents" :key="item.id">
                <Card
                    bordered
                    :class="['veeva-card', 'card', { ['card__bordered']: selectedDocumentId === item.id }]"
                    @click="selectedDocumentId = item.id"
                >
                    <div class="card-thumbnail" :style="{ 'background-image': `url(${item.thumbnialURL})` }" />

                    <div class="card-title">
                        <span>{{ item.name__v.toLocaleUpperCase() }}</span>
                    </div>
                </Card>
            </GridItem>
        </Grid>
    </div>

    <!-- FOOTER -->
    <portal to="media-library-modal-footer">
        <Page v-model="currentPage" :total="paginationMax * pageSize" :page-size="pageSize" />

        <Button :disabled="!selectedDocumentId" :loading="isImportLoading" @click="onImport">
            Import into library
        </Button>
    </portal>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { useMutation } from "@vue/apollo-composable";
import { useDebounceFn } from "@vueuse/core";

import uploadMediaFilesMutation from "@/apollo/mutations/UploadMediaFiles.gql";

import bus from "@/bus";

import { getAndSearchVeevaDocument, syncVeevaDocument, type VeevaDocument } from "./veevaApi";

const { mutate: uploadMediaFiles, onError: onUploadError } = useMutation(uploadMediaFilesMutation);

const { clientId, folderId } = defineProps<{
    clientId: string;
    folderId: string;
}>();

const emit = defineEmits<(e: "assetAdded", assetId: string) => void>();

const documents = ref<VeevaDocument[]>([]);
const selectedDocumentId = ref<number | null>(null);

const pageSize = 28;
const pagination = ref<(number | string)[]>([]);
const paginationMax = ref(0);
const currentPage = defineModel<number>("currentPage", { default: 1 });
const searchQuery = defineModel<string>("searchQuery", { default: "" });

const error = ref<string | null>(null);

const isLoading = ref(false);
const isImportLoading = ref(false);

const generatePagination = () => {
    const total = paginationMax.value;
    const current = currentPage.value + 1;

    if (total <= 7) {
        pagination.value = Array.from({ length: total }, (_, i) => i + 1);
        return;
    }

    if (current < 4) {
        pagination.value = [1, 2, 3, 4, "...", total];
        return;
    }

    if (current >= total - 3) {
        pagination.value = [1, "...", total - 3, total - 2, total - 1, total];
        return;
    }

    pagination.value = [1, "...", current - 1, current, current + 1, "...", total];
};

async function getVeevaDocuments() {
    isLoading.value = true;
    error.value = null;
    selectedDocumentId.value = null;

    try {
        const data = await getAndSearchVeevaDocument({
            query: searchQuery.value,
            page: currentPage.value - 1, // Veeva API uses 0-based index
            pageSize
        });

        isLoading.value = false;

        documents.value = data.documents;
        paginationMax.value = data.pagination.maxPages;

        generatePagination();
    } catch (err) {
        isLoading.value = false;
        console.error("Error fetching Veeva documents:", err);

        error.value = (err as Error).message;
    }
}

watch(currentPage, () => {
    getVeevaDocuments();
});

onMounted(() => {
    getVeevaDocuments();
});

watch(searchQuery, () => {
    onSearch();
});

const onSearch = useDebounceFn(() => {
    currentPage.value = 1;
    getVeevaDocuments();
}, 300);

async function onImport() {
    error.value = null;

    if (selectedDocumentId.value === null) {
        return;
    }

    try {
        isImportLoading.value = true;
        const data = await syncVeevaDocument(selectedDocumentId.value);

        const resp = await uploadMediaFiles({
            clientId,
            folderId,
            files: [
                {
                    name: data.fileName,
                    url: data.documentURL
                }
            ]
        });

        emit("assetAdded", resp?.data.uploadMediaFiles[0].id);
    } catch (err) {
        isImportLoading.value = false;

        console.error("Error importing document:", err);
        error.value = (err as Error).message;
        emitApolloError(err as Error);
    }
}

function emitApolloError(error: Error) {
    bus.$emit("apolloErrorEvent", error);
}

onUploadError(emitApolloError);
</script>

<style>
.veeva-card {
    .ivu-card-body {
        padding: 0 !important;
    }
}
</style>

<style scoped>
.items {
    flex-grow: 1;
}

.query-input {
    width: auto;
    margin: 16px 0;
}

.info-text {
    width: 100%;
    text-align: center;
}

.card {
    cursor: pointer;
    overflow: hidden;
}

.card__bordered {
    border: 1px solid var(--wpp-primary-color-500) !important;
}

.card-thumbnail {
    height: 125px;
    background-color: #f6f6f6;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
}

.card-title {
    height: 40px;
    padding: 10px 16px;
    border-top: 1px solid var(--wpp-grey-color-300);

    text-align: center;
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
